import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import { z } from "zod";
import api, { getBackstageURL, Option, PaginationResponse } from "services/api";

export interface User {
  empNo: string;
  empName: string;
  isHq: boolean;
  isManager: boolean;
  empId: string;
  deptId?: string | null;
  depOu: string | null;
  deptName?: string | null;
  title: string | null;
  email?: string | null;
  tel?: string | null;
  telExt?: string | null;
  fax?: string | null;
  address?: string | null;
  stopYn: boolean;
  isFromAd: boolean;
  updateUser: string | null;
  updateDate: string | null;
  description?: string | null;
  lastLogon?: string;
  roleNos: { roleNo: string; roleName: string }[];
}

export const userSchema = z.object({
  empNo: z
    .string()
    .regex(/(^\w+$)/, "只允許輸入英文與數字")
    .min(1, "員工編號為必填項目"),
  empName: z.string().min(1, "員工名稱為必填項目").max(50, "最長 50 個字"),
  email: z
    .string()
    .email("Email 格式錯誤")
    .min(1, "Email 為必填項目")
    .max(100, "最長 100 個字"),

  deptId: z.string().max(50, "最長 50 個字"),
  deptName: z.string().max(300, "最長 300 個字"),
  tel: z.string().max(20, "最長 20 個字"),
  telExt: z.string().max(10, "最長 10 個字"),
  fax: z.string().max(20, "最長 20 個字"),
  address: z.string().max(500, "最長 500 個字"),
  description: z.string().max(100, "最長 100 個字"),
  password: z.string().max(20, "最長 20 個字").optional(),
  stopYn: z.boolean(),
});

export type CreateUser = z.infer<typeof userSchema>;

export type ResetPasswordUser = {
  empNo: string;
  password: string;
};

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    users: build.query<
      PaginationResponse<User>,
      {
        deptName?: string;
        stopYn?: string;
        isFromAd?: string;
        page?: number;
        role?: string;
        search?: string;
        sort?: string;
        perPage?: number;
      }
    >({
      query: ({
        deptName,
        stopYn,
        isFromAd,
        page = 1,
        role,
        search,
        sort,
        perPage,
      }) => ({
        url: getBackstageURL("/users"),
        params: {
          page,
          perPage: perPage || 30,
          search: search || undefined,
          sort: sort || undefined,
          deptName: deptName || undefined,
          isFromAd: isFromAd || undefined,
          role: role || undefined,
          stopYn: stopYn || undefined,
        },
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.items.map(({ empNo }) => ({
                type: "User" as const,
                id: empNo,
              })),
              "User",
            ]
          : ["User"],
      transformResponse: (res: User[], meta: FetchBaseQueryMeta) => {
        const headerTotalCount = +meta.response?.headers.get("total-count");

        return {
          totalCount: Number.isNaN(headerTotalCount) ? 0 : headerTotalCount,
          items: res ?? [],
        };
      },
    }),
    user: build.query<User, string>({
      query: (id) => getBackstageURL(`/users/${id}`),
      providesTags: (result, error, arg) =>
        result ? [{ type: "User", id: result.empNo }] : [],
    }),
    createUser: build.mutation<User, CreateUser>({
      query: (body) => ({
        url: getBackstageURL("/users"),
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    updateUser: build.mutation<User, CreateUser>({
      query: (body) => ({
        url: getBackstageURL(`/users/${body.empNo}`),
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "User", id: arg.empNo },
      ],
    }),
    deleteUser: build.mutation<void, string>({
      query: (id) => ({
        url: getBackstageURL(`/users/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
    resetPassword: build.mutation<void, ResetPasswordUser>({
      query: (body) => ({
        url: getBackstageURL(`/users/${body.empNo}/reset-password`),
        method: "PUT",
        body,
      }),
    }),
    deptOptions: build.query<Option[], void>({
      query: () => getBackstageURL("/users/dept-options"),
    }),
  }),
  overrideExisting: false,
});

export const {
  useUsersQuery,
  useUserQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useResetPasswordMutation,
  useDeptOptionsQuery,
} = extendedApi;
